import { type FC, useMemo, useRef, useState } from 'react';

import { Accordion } from '@/components/UI/Accordion/Accordion';
import Button from '@/components/UI/Button/Button';
import { _isEmpty } from '@/littledash';
import type { Study, StudyOverviewSectionV1 } from '@/model/Study.model';

import TextSkeleton from '../TextSkeleton';
import type { OverviewSectionKey } from './OverviewAccordion.model';
import styles from './OverviewAccordion.module.scss';
import { buildOverviewReferences, isEmptyOverviewSection, overviewSectionIds } from './OverviewAccordion.utils';
import { OverviewAccordionContext, type OverviewAccordionContextValue } from './OverviewAccordionContext';
import OverviewAccordionSection from './OverviewAccordionSection/OverviewAccordionSection';

interface OverviewAccordionProps {
  sections: Array<StudyOverviewSectionV1>;
  study?: Study;
  studyId?: string;
  loading?: boolean;
  failed?: boolean;
  testId?: string;
  onSectionSaved?: () => void;
  onRetry?: () => void;
}

/**
 * Displays the study-design sections in the order the API returns them and
 * coordinates individual and bulk expand/collapse state.
 */
const OverviewAccordion: FC<OverviewAccordionProps> = ({
  sections,
  study,
  studyId,
  loading = false,
  failed = false,
  testId = 'overview-accordion',
  onSectionSaved,
  onRetry,
}) => {
  const sectionIds = useMemo(() => overviewSectionIds(sections), [sections]);
  // Tracked by section id rather than by position so a background refresh,
  // which hands us a new sections array, keeps whatever the user had expanded.
  const [openSectionIds, setOpenSectionIds] = useState<ReadonlySet<string>>(() => new Set());
  const openItems = useMemo(() => sectionIds.map((id) => openSectionIds.has(id)), [sectionIds, openSectionIds]);
  const setOpenItems = (nextOpenItems: Array<boolean>): void => {
    setOpenSectionIds(new Set(sectionIds.filter((_, index) => nextOpenItems[index])));
  };
  const accordionState: [Array<boolean>, (nextOpenItems: Array<boolean>) => void] = [openItems, setOpenItems];
  const canExpand = sections.map((section) => !isEmptyOverviewSection(section));
  const containerRef = useRef<HTMLElement | null>(null);
  const references = useMemo(() => buildOverviewReferences(sections), [sections]);
  const contextValue: OverviewAccordionContextValue = {
    ...references,
    revealSection: (type: OverviewSectionKey) => {
      const index = sections.findIndex((section) => section.type === type);
      if (index < 0 || !canExpand[index]) {
        return;
      }
      const sectionId = sectionIds[index];
      setOpenSectionIds((current) => new Set(current).add(sectionId));
      containerRef.current
        ?.querySelector(`[data-testid="overview-accordion-${sectionId}-header"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  };
  const allExpanded = openItems.every((isOpen, index) => !canExpand[index] || isOpen);
  const allCollapsed = openItems.every((isOpen, index) => !canExpand[index] || !isOpen);

  if (loading) {
    return (
      <section
        className={styles.container}
        data-test-component="OverviewAccordion"
        data-test-element="loading"
        data-testid={testId}
      >
        {Array.from({ length: 3 }, (_, index) => (
          <div className={styles.skeleton} key={index}>
            <TextSkeleton width="30%" />
          </div>
        ))}
      </section>
    );
  }

  if (failed) {
    return (
      <section
        className={styles.container}
        data-test-component="OverviewAccordion"
        data-test-element="error-state"
        data-testid={testId}
      >
        <p className="f6 lh-copy mid-gray mt0 mb3">Unable to load study design.</p>
        <Button outline onClick={onRetry} testId="overview-accordion-retry">
          Retry
        </Button>
      </section>
    );
  }

  if (_isEmpty(sections)) {
    return null;
  }

  return (
    <section
      ref={containerRef}
      className={styles.container}
      data-test-component="OverviewAccordion"
      data-test-element="container"
      data-testid={testId}
    >
      <div className={styles.actions}>
        <button
          type="button"
          className="link-button"
          disabled={allExpanded}
          onClick={() => setOpenItems(canExpand)}
          data-testid="overview-accordion-expand-all"
        >
          Expand all
        </button>
        <button
          type="button"
          className="link-button"
          disabled={allCollapsed}
          onClick={() => setOpenItems(sections.map(() => false))}
          data-testid="overview-accordion-collapse-all"
        >
          Collapse all
        </button>
      </div>

      <OverviewAccordionContext value={contextValue}>
        <Accordion className={styles.accordion} allowMultipleExpanded outerState={accordionState}>
          {sections.map((section, index) => (
            <OverviewAccordionSection
              key={sectionIds[index]}
              sectionId={sectionIds[index]}
              section={section}
              study={study}
              studyId={studyId}
              onSectionSaved={onSectionSaved}
            />
          ))}
        </Accordion>
      </OverviewAccordionContext>
    </section>
  );
};

export default OverviewAccordion;
